<template>
    <div class="create_line">
        <ContextMenu ref="contextMenu">
            <template #trigger>
                <div class="btn-new btn-notransform" tabindex="0">
                    <i class="bi bi-plus text_sec"></i>
                </div>
            </template>
            <ContextMenuItem v-if="!hideCreateProject" icon="bi bi-folder" @click="openCreateProject">
                {{ $t('create.createProject') }}
            </ContextMenuItem>
            <ContextMenuSeparator v-if="!hideCreateProject" />
            <ContextMenuItem icon="bi bi-soundwave" @click="triggerUpload">
                {{ $t('create.uploadTrack') }}
            </ContextMenuItem>
        </ContextMenu>
        <UploadComponent ref="uploadComponent" :project-id="projectId" class="create-button__upload-host" />
    </div>

    <SheetModal v-if="!hideCreateProject" v-model="createNewProjectSheet">
        <div class="container">
            <div class="row justify-content-center">
                <div class="col-md-8">
                    <h2>{{ $t('create.newProject') }}</h2>
                    <div class="row">
                        <div class="col-4">
                            <CoverUpload v-model="newProjectCover" @change-state="isCoverLoadingState">
                                <template #button="{ loading }">
                                    <div v-if="coverPreview" class="cover-preview-container"
                                        :style="{ backgroundImage: `url(${coverPreview})` }">
                                        <div class="project-buttons-overlay" v-if="!isCoverLoading" @click.stop>
                                            <button class="project-button" @click.stop>
                                                <i class="bi bi-card-image"></i>
                                            </button>
                                            <button class="project-button" @click.stop="removeCover">
                                                <i class="bi bi-trash"></i>
                                            </button>
                                        </div>
                                        <div class="project-buttons-overlay project-buttons-overlay-constant"
                                            v-if="isCoverLoading">
                                            <div class="spinner-border" role="status">
                                                <span class="visually-hidden">{{ $t('common.loading') }}</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div v-else class="upload-placeholder">
                                        <i v-if="loading" class="bi bi-hourglass-split"></i>
                                        <i v-else class="bi bi-cloud-upload"></i>
                                        <div>{{ loading ? $t('create.loading') : $t('create.uploadCover') }}</div>
                                    </div>
                                </template>
                            </CoverUpload>
                        </div>
                        <div class="col-8">
                            <span class="sec">{{ $t('create.artist') }}</span>
                            <input class="input_thing" placeholder="Van Halen" v-model="newProjectArtist">
                            <span class="sec">{{ $t('create.name') }}</span>
                            <input class="input_thing" placeholder="Van Halen II" v-model="newProjectName">
                            <div class="btn-new mt-4" v-on:click="this.createProject()">{{ $t('create.create') }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </SheetModal>


    <SheetModal v-model="subscribeSheet">
        <SubscribeComponent
            :context-title="$t('subscribe.cloudRequiredTitle')"
            :context-message="$t('subscribe.cloudRequiredMessage')"
        />
    </SheetModal>

</template>


<script>

import SheetModal from './UI/SheetModal.vue';
import CoverUpload from './CoverUpload.vue';
import { uuidv4 } from '@/scripts/uuid.js';
import { hapticMixin } from '@/scripts/haptic';
import storageService from '@/scripts/services/StorageService';
import UploadComponent from './UploadComponent.vue';
import ContextMenu from '@/components/UI/ContextMenu.vue';
import ContextMenuItem from '@/components/UI/ContextMenuItem.vue';
import ContextMenuSeparator from '@/components/UI/ContextMenuSeparator.vue';
import SubscribeComponent from './SubscribeComponent.vue';

export default {
    name: "CreateButton.vue",
    components: { UploadComponent, SheetModal, CoverUpload, ContextMenu, ContextMenuItem, ContextMenuSeparator, SubscribeComponent },
    mixins: [hapticMixin],
    props: {
        hideCreateProject: {
            type: Boolean,
            default: false
        },
        projectId: {
            type: String,
            default: null
        }
    },
    computed: {
        projects() {
            return storageService.state.projects
        },
        savedFiles() {
            return storageService.state.tracks
        },
        hasActiveSubscription(){
            return storageService.hasActiveSubscription()
        }
    },

    data() {
        return {
            full: false,
            disableTimeout: false,
            createNewProjectSheet: false,
            newProjectName: "",
            newProjectArtist: "",
            toggledTracks: [],
            newProjectCover: "",
            coverPreview: "",
            isCoverLoading: false,
            subscribeSheet: false,
            skipNextCoverCleanup: false
        }
    },
    watch: {
        newProjectCover: {
            immediate: true,
            async handler(newCoverId, oldCoverId) {
                this.updateCoverPreview(newCoverId);
                if (this.skipNextCoverCleanup) {
                    this.skipNextCoverCleanup = false;
                    return;
                }
                await storageService.deleteCoverImageIfReplaced(oldCoverId, newCoverId);
            }
        }
    },
    methods: {
        openCreateProject() {
            this.createNewProjectSheet = true;
            this.$refs.contextMenu?.close();
            this.triggerHaptic();
        },
        openSubscribe(){
            this.subscribeSheet = true
            this.$refs.contextMenu?.close();
            this.triggerHaptic();
        },
        triggerUpload() {
            this.$refs.uploadComponent?.triggerFileSelect();
            this.$refs.contextMenu?.close();
            this.triggerHaptic();
        },
        activateButton() {
            this.triggerHaptic();
            this.full = true
            setTimeout(() => {
                this.full = false
            }, 10000)
        },
        toggleTrack(track) {
            // if file in toggledTracks then remove it, if not - add
            if (this.toggledTracks.includes(track)) {
                this.toggledTracks.splice(this.toggledTracks.indexOf(track), 1)
            } else {
                this.toggledTracks.push(track)
            }
            console.log(this.toggledTracks)
        },
        async createProject() {
            const newProj = {
                id: uuidv4(),
                artist: this.newProjectArtist,
                name: this.newProjectName,
                tracks: this.toggledTracks,
                cover: this.newProjectCover || null // Use uploaded cover or default
            }
            const isCreated = await storageService.createProject(newProj)
            if (!isCreated) return

            this.createNewProjectSheet = false
            this.newProjectArtist = ""
            this.newProjectName = ""
            this.skipNextCoverCleanup = Boolean(this.newProjectCover)
            this.newProjectCover = ""
            this.toggledTracks = []
        },
        async updateCoverPreview(coverId) {
            const nocoverurl = 'img/nocover.png'
            if (!coverId || coverId === nocoverurl) {
                this.coverPreview = nocoverurl;
                return;
            }
            try {
                const imageUrl = await storageService.getCoverUrl(coverId);
                this.coverPreview = imageUrl || nocoverurl;
            } catch (error) {
                console.error("Error getting cover image:", error);
                this.coverPreview = nocoverurl;
            }
        },
        async removeCover() {
            await storageService.deleteCoverImage(this.newProjectCover);
            this.newProjectCover = '';
        },
        isCoverLoadingState(val) {
            this.isCoverLoading = val
        }
    }
}

</script>

<style scoped>

.cover-preview-container {
    position: relative;
    width: 100%;
    padding-top: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: rgba(0, 0, 0, 0.15) 0px 5px 15px 0px;
    cursor: pointer;
}

.project-buttons-overlay {
    position: absolute;
    bottom: 10px;
    left: 0;
    right: 0;
    display: flex;
    gap: 10px;
    justify-content: center;
    z-index: 2;
    opacity: 0;
    transition: opacity 0.3s ease;
}

.project-buttons-overlay-constant {
    opacity: 1;
}

.cover-preview-container:hover .project-buttons-overlay {
    opacity: 1;
}

.project-button {
    background: rgba(47, 47, 47, 0.55);
    box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);
    backdrop-filter: blur(12.3px);
    -webkit-backdrop-filter: blur(12.3px);
    color: white;
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.project-button:hover {
    background-color: rgba(47, 47, 47, 0.88);
}

.project-button.danger {
    background: rgba(220, 53, 69, 0.8);
    box-shadow: 0 4px 30px rgba(220, 53, 69, 0.1);
    backdrop-filter: blur(12.3px);
    -webkit-backdrop-filter: blur(12.3px);
}

.project-button.danger:hover {
    background: rgba(220, 53, 69, 0.9);
}

.upload-placeholder {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    border: 2px dashed #ccc;
    border-radius: 8px;
    text-align: center;
    color: #666;
    transition: all 0.3s ease;
    cursor: pointer;
    aspect-ratio: 1;
}

.upload-placeholder:hover {
    border-color: #999;
    color: #333;
    background-color: #f9f9f9;
}

.upload-placeholder i {
    font-size: 2rem;
    margin-bottom: 0.5rem;
}

.create_line{
    display: flex;
    gap: 1rem;
    justify-content: center;
}

.create-button__upload-host {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    opacity: 0;
    pointer-events: none;
}
</style>
